/**
 * Field.jsx
 * ---------------------------------------------------------------------------
 * A single primitive that renders a text input, date input, textarea, or
 * select depending on props. Uses underlined styling (no boxes) to match
 * the refined, editorial aesthetic.
 *
 * Props:
 *   label       — small uppercase label above the input
 *   name        — form field name
 *   value       — controlled value
 *   onChange    — change handler
 *   type        — input type ("text" | "email" | "date" | ...)
 *   placeholder — placeholder text
 *   textarea    — render a textarea instead of input
 *   options     — if provided, renders a <select> with these {value,label}s
 */

import React from "react";
import { tokens } from "../../lib/tokens.js";

const inputStyle = {
  width: "100%",
  background: "transparent",
  border: "none",
  borderBottom: `1px solid ${tokens.hairline}`,
  padding: "12px 0",
  fontFamily: "'Inter', sans-serif",
  fontSize: 15,
  color: tokens.ink,
  outline: "none",
  borderRadius: 0,
  transition: "border-color 180ms ease",
};

const labelStyle = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: tokens.inkSoft,
  display: "block",
  marginBottom: 6,
};

const selectArrow = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%230B1F3B' fill='none' stroke-width='1.2'/></svg>")`;

export default function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  textarea,
  options,
}) {
  const onFocus = (e) => (e.target.style.borderBottomColor = tokens.ink);
  const onBlur = (e) => (e.target.style.borderBottomColor = tokens.hairline);

  return (
    <div style={{ marginBottom: 28 }}>
      <label style={labelStyle} htmlFor={name}>
        {label}
      </label>

      {textarea ? (
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={4}
          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      ) : options ? (
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          style={{
            ...inputStyle,
            appearance: "none",
            backgroundImage: selectArrow,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 4px center",
            paddingRight: 24,
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          <option value="">Select…</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={inputStyle}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      )}
    </div>
  );
}
